/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useRef, useState } from "react"
import { ThumbsDownOutlined, ThumbsUpOutlined } from "@duxton-web/icons"
import { Button, Checkbox, Grid, Input, Typography } from "@duxton-web/ui"
import classNames from "classnames"

import { useTranslationsMap } from "common/utils/i18n"

import styles from "./PmfFeedbackContent.modules.css"

export const PMF_FEEDBACK_RATING = {
  GOOD: "GOOD",
  BAD: "BAD",
} as const

export type PmfFeedbackRating = typeof PMF_FEEDBACK_RATING[keyof typeof PMF_FEEDBACK_RATING]

export const PMF_FEEDBACK_TOPIC = {
  PRODUCT_CREATE_EDIT: "PRODUCT_CREATE_EDIT",
  STOCK_MANAGEMENT: "STOCK_MANAGEMENT",
  PRODUCT_MEDIA: "PRODUCT_MEDIA",
  CATALOG_ACROSS_STORES: "CATALOG_ACROSS_STORES",
} as const

export type PmfFeedbackTopic = typeof PMF_FEEDBACK_TOPIC[keyof typeof PMF_FEEDBACK_TOPIC]

const RATINGS = Object.values(PMF_FEEDBACK_RATING)
const TOPICS = Object.values(PMF_FEEDBACK_TOPIC)

const RATING_ICONS: Record<PmfFeedbackRating, React.ReactElement> = {
  [PMF_FEEDBACK_RATING.GOOD]: <ThumbsUpOutlined aria-hidden />,
  [PMF_FEEDBACK_RATING.BAD]: <ThumbsDownOutlined aria-hidden />,
}

export interface PmfFeedbackPayload {
  rating: PmfFeedbackRating
  topics: PmfFeedbackTopic[]
  comment: string
}

export type PmfFeedbackState = Omit<PmfFeedbackPayload, "rating"> & {
  rating: PmfFeedbackRating | null
}

interface PmfFeedbackContentProps {
  reserveCloseButtonSpace?: boolean
  onRatingChange?: (rating: PmfFeedbackRating) => void
  onStateChange?: (state: PmfFeedbackState) => void
  onSubmit?: (payload: PmfFeedbackPayload) => void
}

export const PmfFeedbackContent: FC<PmfFeedbackContentProps> = ({
  reserveCloseButtonSpace = false,
  onRatingChange,
  onStateChange,
  onSubmit,
}) => {
  const [rating, setRating] = useState<PmfFeedbackRating | null>(null)
  const [topics, setTopics] = useState<PmfFeedbackTopic[]>([])
  const [comment, setComment] = useState("")
  const contentEndRef = useRef<HTMLDivElement>(null)

  const { md } = Grid.useBreakpoint()
  const isExpanded = rating !== null
  const showHeaderHeading = md !== false || !isExpanded

  useEffect(() => {
    onStateChange?.({ rating, topics, comment })
  }, [rating, topics, comment, onStateChange])

  const textMap = useTranslationsMap({
    heading: "ecommerce.sellerCentre.pmfFeedback.heading",
    good: "ecommerce.sellerCentre.pmfFeedback.good",
    bad: "ecommerce.sellerCentre.pmfFeedback.bad",
    whatDidYouLike: "ecommerce.sellerCentre.pmfFeedback.whatDidYouLike",
    whatCanWeImprove: "ecommerce.sellerCentre.pmfFeedback.whatCanWeImprove",
    selectMoreThanOne: "ecommerce.sellerCentre.pmfFeedback.selectMoreThanOne",
    creatingAndEditingProducts:
      "ecommerce.sellerCentre.pmfFeedback.creatingAndEditingProducts",
    stockManagement: "ecommerce.sellerCentre.pmfFeedback.stockManagement",
    photosVideoAndProductLook:
      "ecommerce.sellerCentre.pmfFeedback.photosVideoAndProductLook",
    managingOneCatalogAcrossStores:
      "ecommerce.sellerCentre.pmfFeedback.managingOneCatalogAcrossStores",
    otherFeedback: "ecommerce.sellerCentre.pmfFeedback.otherFeedback",
    commentPlaceholder: "ecommerce.sellerCentre.pmfFeedback.commentPlaceholder",
    submit: "ecommerce.sellerCentre.pmfFeedback.submit",
  })

  const RATING_LABELS: Record<PmfFeedbackRating, string> = {
    [PMF_FEEDBACK_RATING.GOOD]: textMap.good,
    [PMF_FEEDBACK_RATING.BAD]: textMap.bad,
  }

  const TOPIC_LABELS: Record<PmfFeedbackTopic, string> = {
    [PMF_FEEDBACK_TOPIC.PRODUCT_CREATE_EDIT]:
      textMap.creatingAndEditingProducts,
    [PMF_FEEDBACK_TOPIC.STOCK_MANAGEMENT]: textMap.stockManagement,
    [PMF_FEEDBACK_TOPIC.PRODUCT_MEDIA]: textMap.photosVideoAndProductLook,
    [PMF_FEEDBACK_TOPIC.CATALOG_ACROSS_STORES]:
      textMap.managingOneCatalogAcrossStores,
  }

  const TOPIC_SECTION_HEADINGS: Record<PmfFeedbackRating, string> = {
    [PMF_FEEDBACK_RATING.GOOD]: textMap.whatDidYouLike,
    [PMF_FEEDBACK_RATING.BAD]: textMap.whatCanWeImprove,
  }

  const toggleTopic = (topic: PmfFeedbackTopic) => {
    setTopics(current =>
      current.includes(topic)
        ? current.filter(item => item !== topic)
        : [...current, topic],
    )
  }

  const scrollContentEndIntoView = () => {
    const scroll = () => {
      contentEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      })
    }
    // Wait for the mobile keyboard and drawer resize before scrolling to the end.
    setTimeout(scroll, 250)
  }

  const isSubmitDisabled = topics.length === 0 && comment.trim().length === 0

  const handleSubmit = () => {
    if (!rating || isSubmitDisabled) return
    onSubmit?.({ rating, topics, comment })
  }

  return (
    <>
      <div
        className={classNames(styles.header, {
          [styles.headerWithCloseButton]: reserveCloseButtonSpace,
        })}
      >
        {showHeaderHeading && (
          <Typography.Body level={1} strong className={styles.title}>
            {textMap.heading}
          </Typography.Body>
        )}
        <div className={styles.ratings}>
          {RATINGS.map(value => {
            const isSelected = rating === value
            return (
              <Button
                key={value}
                size="large"
                type="secondary"
                icon={RATING_ICONS[value]}
                className={classNames(styles.ratingButton, {
                  [styles.ratingButtonSelected]: isSelected,
                })}
                onClick={() => {
                  setRating(value)
                  onRatingChange?.(value)
                }}
                aria-pressed={isSelected}
              >
                {RATING_LABELS[value]}
              </Button>
            )
          })}
        </div>
      </div>
      {isExpanded && (
        <div className={styles.form}>
          <div className={styles.formIntro}>
            <Typography.Body level={1} strong className={styles.title}>
              {TOPIC_SECTION_HEADINGS[rating]}
            </Typography.Body>
            <Typography.Metadata level={1}>
              {textMap.selectMoreThanOne}
            </Typography.Metadata>
          </div>
          <div className={styles.topics}>
            {TOPICS.map(topic => (
              <Checkbox
                key={topic}
                checked={topics.includes(topic)}
                onChange={() => toggleTopic(topic)}
                className={styles.topic}
              >
                {TOPIC_LABELS[topic]}
              </Checkbox>
            ))}
          </div>
          <div className={styles.otherFeedback}>
            <Typography.Body level={1} strong className={styles.title}>
              {textMap.otherFeedback}
            </Typography.Body>
            <Input.TextArea
              value={comment}
              onChange={event => setComment(event.target.value)}
              onFocus={md === false ? scrollContentEndIntoView : undefined}
              placeholder={textMap.commentPlaceholder}
              autoComplete="off"
              className={styles.comment}
              maxLength={500}
              showCount
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
          </div>
          <Button
            type="primary"
            block
            size="large"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            {textMap.submit}
          </Button>
          <div ref={contentEndRef} className={styles.contentEnd} aria-hidden />
        </div>
      )}
    </>
  )
}
